import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { roleCheck } from "utils/index";
import { Spinner } from "react-bootstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import {
  stringEllipsis,
  VIRTUAL_DRAFTING,
  ADMIN,
  VIRTUAL_DRAFTING_MANAGER,
  MAX_SIZE_UPLOADED_FILE_TEXT,
  MAX_SIZE_UPLOADED_FILE,
  ASSIGN_OVERSEAS_MANAGER,
  DRAFTING_OVERSEAS
} from "../../../../../../../utils/constants";
import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../../../../reducers";
import CommentBox from "../CommentBox";
import FlagJobServiceModal from "../../../modals/FlagJobServiceModal";
import DeleteFile from "../../DeleteFile";
import { uploadFile, updateJobServiceTransition } from "../../../../../../../requesters/jobServiceRequester";
import ConfirmationModal from "../../../modals/ConfirmationModal";

const VirtualDraftingStage = ({
  jobService, reloadData, forAdditionalWork
}) => {
  const {
    id, attributes: {
      vrDraftedDocuments,
      primaryVrDraftsmanName,
      primaryVrDraftsmanId,
      jobNo,
      jobTitle,
      forProjectX,
      projectXJobNo
    }
  } = jobService
  const [comment, setComment] = useState("")
  const [showError, setShowError] = useState(false)
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [requesting, setRequesting] = useState(false)
  const [showTransitionConfirmation, setShowTransitionConfirmation] = useState(false)
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const systemRole = currentUser.attributes.role
  const currentRole = useSelector(getCurrentRole)
  const currentTeamRole = currentUser.attributes.current_team_role
  const isVrDraftingTeam = roleCheck(systemRole, currentRole, VIRTUAL_DRAFTING)
  const isAdmin = currentRole.includes(ADMIN) || systemRole === ADMIN
  const isVrDraftingManager = roleCheck(systemRole, currentTeamRole, VIRTUAL_DRAFTING_MANAGER)

  const assignedMainVR = primaryVrDraftsmanName
  const displayJobNo = forProjectX ? projectXJobNo : jobNo

  const sendToDrafting = () => {
    setRequesting(true)
    updateJobServiceTransition(baseUrl, id, ASSIGN_OVERSEAS_MANAGER, comment, null, DRAFTING_OVERSEAS)
    .then(res => {
      if (res.status === 200) {
        window.location.reload()
      }
    })
    // eslint-disable-next-line no-console
    .catch(err => console.log(err))
    .finally(() => setRequesting(false))
  }

  const actionButtons = () => {
    const shouldDisableMoving = !assignedMainVR || requesting || (!isVrDraftingManager && primaryVrDraftsmanId.toString() !== currentUser.id.toString())

    return (
      <div className="mr-2">
        <button
          className={`btn ${assignedMainVR ? "btn-outline-primary" : "btn-outline-secondary"}`}
          type="button"
          disabled={shouldDisableMoving}
          onClick={() => setShowTransitionConfirmation(true)}>
          Drafting Required
        </button>
        <ConfirmationModal
          jobNo={displayJobNo}
          jobTitle={jobTitle}
          show={showTransitionConfirmation}
          resetState={() => setShowTransitionConfirmation(false)}
          sendTo={sendToDrafting}
          messageType="Drafting" />
      </div>
    )
  }

  const UploadFileSection = () => {
    const temp = 1

    return (
      <React.Fragment>
        {(assignedMainVR || vrDraftedDocuments?.length > 0) && (
          <>
            <div className="input-group mt-2 mb-3">
              <div className="custom-file">
                <input
                  className="custom-file-input"
                  id="customFile"
                  type="file"
                  onChange={e => setUploadedFile(e.currentTarget.files[0])} />
                <label className="custom-file-label ml-2" htmlFor="customFile">
                  Choose file
                </label>
              </div>
              {uploading && (
                <Spinner
                  animation="border"
                  className="ml-3"
                  variant="secondary" />
              )}
            </div>

            {!!vrDraftedDocuments?.length && vrDraftedDocuments.map(file => (
              <div className="ml-2 mt-2" key={file?.data?.id}>
                <a
                  className="mr-2"
                  href={file?.data?.attributes?.filePath}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={file?.data?.attributes?.fileName}>
                  {stringEllipsis(file?.data?.attributes?.fileName, 20, 25)}
                </a>

                {(isVrDraftingManager || currentUser.id === file?.data?.attributes?.createdByUserId) && (
                  <DeleteFile
                    className=""
                    file={file}
                    reloadData={reloadData} />
                )}
              </div>
            ))}
          </>
        )}
      </React.Fragment>
    )
  }

  const AddFlagSection = () => {
    const allowedToFlag = assignedMainVR

    return (
      <React.Fragment>
        { allowedToFlag && (
          <button className="btn" type="button" onClick={() => { setShowFlagModal(true) }}>
            <i className="bi bi-flag-fill mr-2"></i>
            Add Flag
          </button>
        )}
        <FlagJobServiceModal
          show={showFlagModal}
          onHide={() => { setShowFlagModal(false) }}
          jobService={jobService}
          reloadData={reloadData} />
      </React.Fragment>
    )
  }

  useEffect(() => {
    if (uploadedFile) {
      if (uploadedFile.size > MAX_SIZE_UPLOADED_FILE) {
        setShowError(true)
      } else {
        const documentCategory = "VR Drafted Documents"
        const formData = new FormData();
        formData.append("file", uploadedFile)
        formData.append("document_category", documentCategory)
        formData.append("created_by_user_id", currentUser.id)
        setUploading(true)
        uploadFile(baseUrl, id, formData)
          .then(res => {
            if (res.status === 200) {
              setUploading(false)
              reloadData()
            }
          })
          // eslint-disable-next-line no-console
          .catch(err => console.log(err))
      }
    }
  }, [uploadedFile])

  return (
    <div className="timeline-item mt-2">
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4">
          <div className="row">
            <div className="col-sm-6">
              {UploadFileSection()}
            </div>

            <SweetAlert
              show={showError}
              type="error"
              title="File size exceeded!"
              closeOnClickOutside
              allowEscape={false}
              onConfirm={() => setShowError(false)}>
              The File is too large (Max size is
              {" "}
              {MAX_SIZE_UPLOADED_FILE_TEXT}
              )
            </SweetAlert>

            { !forAdditionalWork && (
              <div className="col-sm-6 text-right">
                {AddFlagSection()}
              </div>
            )}
          </div>
        </div>
      </div>
      {(isAdmin || isVrDraftingTeam) && !forAdditionalWork && (
        <>
          <CommentBox comment={comment} setComment={setComment} />
          <div className="row mt-4">
            <div className="mr-4 pt-2">Send to</div>
            {actionButtons()}
          </div>
        </>
      )}
    </div>
  )
}

export default VirtualDraftingStage